import { Box, Button, Fade, Flex } from "@chakra-ui/react";
import CreateOrgFirst from "./CreateOrgFirst";
import CreateOrgSecond from "./CreateOrgSecond";
import CreateOrgThird from "./CreateOrgThird";
import { useState } from "react";
import StepIndicator from "./StepIndicator";
import { encodeFunctionData, toHex, keccak256, toBytes } from "viem";
import capsule from "@/constants/capsule";
import { useUserInfo } from "@/context/UserInfo";
import { didAddresses } from "@/constants/addresses";
import { DEFAULT_CHAIN_ID } from "@/constants";
import { createBundler } from "@/constants/bundler";
import usePublicClient from "@/hooks/usePublicClient";
import { updateAvatar } from "@/api/login";

const RequestCreatedTopic = keccak256(
  toBytes("RequestCreated(uint256,address,uint256,bytes)")
);

const NewOrg = () => {
  const publicClient = usePublicClient();
  const [loading, setLoading] = useState(false);
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [step, setStep] = useState(0);
  const { handleSetOrgName, smartAccount, handleSetAvatarIndex } =
    useUserInfo();

  // 创建账号并创建组织
  const handleConfirm = async (orgName: string) => {
    try {
      const isActive = await capsule.isSessionActive();
      if (!isActive) {
        // Handle expired session
        await capsule.refreshSession(false);
        return;
      }
    } catch (error) {
      console.error("Session check failed:", error);
    }

    setLoading(true);

    const transferCallData = await smartAccount!.encodeCalls([
      {
        to: didAddresses[DEFAULT_CHAIN_ID]!,
        value: 0n,
        data: encodeFunctionData({
          abi: [
            {
              type: "function",
              name: "registerSelf",
              inputs: [
                {
                  name: "name",
                  type: "bytes32",
                  internalType: "bytes32",
                },
              ],
              outputs: [],
              stateMutability: "nonpayable",
            },
          ],
          args: [toHex(orgName, { size: 32 })],
        }),
      },
    ]);

    try {
      const bundler = await createBundler(smartAccount!);
      const tx = await bundler.sendTransaction(transferCallData);
      const status = await tx.wait();
      const txHash = status.transactionHash;

      const receipt = await publicClient.waitForTransactionReceipt({
        hash: txHash as `0x${string}`,
      });

      const requestCreatedLog = receipt.logs.find((log) => {
        // 这里需要填入 RequestCreated 事件的实际 topic0
        return log.topics[0] === RequestCreatedTopic;
      });

      if (requestCreatedLog) {
        // 解析日志中的 id（第一个索引参数）
        const requestId = BigInt(requestCreatedLog.topics[1]!);
        console.log("Request ID:", requestId);
      }

      handleSetOrgName(orgName);
      setStep(1);
    } catch (error) {
      console.error("发送失败:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmAvatar = async (avatarIndex: number) => {
    setAvatarLoading(true);

    try {
      const res = await updateAvatar(avatarIndex);
      console.log(res, "res");
      handleSetAvatarIndex(avatarIndex);
      setStep(2);
    } catch (error) {
      console.error("发送失败:", error);
    } finally {
      setAvatarLoading(false);
    }
  };

  return (
    <Flex
      sx={{ padding: "20px", height: "100%" }}
      flexDirection={"column"}
      alignItems={"center"}
    >
      <StepIndicator currentStep={step} step={3}></StepIndicator>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          "& > div": {
            flex: 1,
          },
        }}
      >
        <Fade in={step === 0} unmountOnExit style={{ height: "100%" }}>
          <CreateOrgSecond handleConfirm={handleConfirm} loading={loading} />
        </Fade>
        <Fade in={step === 1} unmountOnExit style={{ height: "100%" }}>
          <CreateOrgFirst
            handleStep={handleConfirmAvatar}
            loading={avatarLoading}
          />
        </Fade>
        <Fade in={step === 2} unmountOnExit>
          <CreateOrgThird />
        </Fade>
      </Box>
    </Flex>
  );
};

export default NewOrg;
